// hooks/usePaintEraser.js
import { useEffect, useRef, useContext } from 'react'
import { fabric } from 'fabric'
import { MODESTATE, OPERATETYPE } from '@view/page_v3/AiEdit/CONST'
import { AIEditContext } from "../../../AIEditContext";

const PAINT_COLOR = 'rgba(30, 144, 255, 0.8)'
const ERASE_COLOR = 'rgba(0,0,0,0)'

const usePaintEraser = ({
    fabricInstanceRef,
    modeStateRef,
    modeState,
    props,
    pencilBrushRef,
    circleMouseRef,
    sizeRef,
    operateTypeRef
}) => {
    const tempPathRef = useRef()
    const { operateType } = useContext(AIEditContext)

    useEffect(() => {
        if (!fabricInstanceRef.current) return
        modeStateRef.current = modeState

        const brush = new fabric.PencilBrushCustom(fabricInstanceRef.current)
        pencilBrushRef.current = brush

        brush.color = modeStateRef.current === MODESTATE.ERASER ? ERASE_COLOR : PAINT_COLOR
        brush.width = props.size
        brush.pathColor = 'rgba(0,0,0,1)'
        brush.globalCompositeOperation = modeStateRef.current === MODESTATE.ERASER ? 'destination-out' : 'source-over'
        brush.limitedToCanvasSize = true

        if (operateTypeRef.current === OPERATETYPE.DRAW) { // 仅在绘制模式创建画笔
            fabricInstanceRef.current.isDrawingMode = true;
            fabricInstanceRef.current.freeDrawingBrush = pencilBrushRef.current;

            // 移除旧的圆形指示器
            if (circleMouseRef.current) {
                fabricInstanceRef.current.remove(circleMouseRef.current);
            }

            const circle = new fabric.Circle({
                left: 0,
                top: 0,
                radius: props.size / 2,
                fill: modeStateRef.current === MODESTATE.ERASER ? 'rgba(230, 62, 62, 0.3)' : 'rgba(30, 144, 255, 0.3)',
                strokeWidth: 2,
                stroke: modeStateRef.current === MODESTATE.ERASER ? 'rgba(240, 72, 72, 1)' : 'rgba(30, 144, 255, 1)',
                originX: 'center',
                originY: 'center',
                hasControls: false,
                hasBorders: false,
                selectable: false,
            })
            circleMouseRef.current = circle
            fabricInstanceRef.current.add(circle)
            fabricInstanceRef.current.bringToFront(circle)
        } else {
            fabricInstanceRef.current.isDrawingMode = false;
        }
        fabricInstanceRef.current.freeDrawingCursor = 'pointer'
    }, [modeState, fabricInstanceRef.current, operateType])


    const updatePathPosition = (event) => {
        if (!fabricInstanceRef.current || !circleMouseRef.current) return;

        const { offsetX, offsetY } = event.e;
        const canvasOffsetX = fabricInstanceRef.current.viewportTransform[4];
        const canvasOffsetY = fabricInstanceRef.current.viewportTransform[5];
        const zoom = fabricInstanceRef.current.getZoom();

        const x = (offsetX - canvasOffsetX) / zoom;
        const y = (offsetY - canvasOffsetY) / zoom;

        if (Array.isArray(circleMouseRef.current) && circleMouseRef.current) {
            circleMouseRef.current.find(item => item.type === 'circle')?.set({ left: x, top: y });
        } else {
            circleMouseRef.current.set({ left: x, top: y });
        }
        fabricInstanceRef.current.renderAll();
    }

    const updatePathWhenEarse = () => {
        if (tempPathRef.current) fabricInstanceRef.current.remove(tempPathRef.current)
        let _points = []

        if (pencilBrushRef.current.decimate) {
            _points = pencilBrushRef.current.decimatePoints(
                pencilBrushRef.current._points,
                pencilBrushRef.current.decimate
            );
        }
 
        if (_points.length === 0) return
        const pathData = pencilBrushRef.current.convertPointsToSVGPath(_points)
        if (pencilBrushRef.current._isEmptySVGPath(pathData)) return

        const tempPath = new fabric.Path(pathData, {
            fill: null,
            stroke: 'rgba(0,0,0,1)',
            strokeWidth: sizeRef.current,
            strokeLineCap: 'round',
            strokeMiterLimit: 10,
            strokeLineJoin: 'round',
            strokeDashArray: null,
            globalCompositeOperation: 'destination-out',
            selectable: false,
            hoverCursor: 'pointer',
            evented: false,
        })
        tempPathRef.current = tempPath
        fabricInstanceRef.current.add(tempPath)
        fabricInstanceRef.current.requestRenderAll()
    }

    const mouseUpWhenEarse = () => {
        if (tempPathRef.current) {
            fabricInstanceRef.current.remove(tempPathRef.current)
            fabricInstanceRef.current.renderAll()
        }
        pencilBrushRef.current._reset()
    }

    return {
        updatePathPosition,
        updatePathWhenEarse,
        mouseUpWhenEarse,
    }
}

export default usePaintEraser